<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@lumeratec/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments, messageType } =
  useMessageContext();
const store = useStore();
const { isAdmin } = useAdmin();

const attachment = computed(() => {
  return attachments.value[0];
});

const isStaffSticker = computed(() => {
  const filenames = [
    attachment.value?.filename,
    attachment.value?.fileName,
    attachment.value?.file_name,
  ];
  const hasStickerFilename = filenames.some(name =>
    String(name || '').startsWith('staff-sticker-')
  );
  const isSmallWebp =
    String(attachment.value?.extension || '').toLowerCase() === 'webp' &&
    Number(attachment.value?.fileSize || attachment.value?.file_size || 0) <=
      102400;

  return hasStickerFilename || isSmallWebp;
});
const imageClass = computed(() =>
  isStaffSticker.value ? 'max-w-40 max-h-40 object-contain' : 'max-w-full'
);

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);
const isDescribing = ref(false);
const imageDescription = ref('');
const imageModeration = ref({});

const accountId = computed(() => store.getters.getCurrentAccountId);
const canDescribeImage = computed(
  () =>
    messageType.value === 0 &&
    store.getters['accounts/isFeatureEnabledonAccount'](
      accountId.value,
      FEATURE_FLAGS.LUMERAI_TASKS
    )
);

const imageAltText = computed(
  () =>
    imageDescription.value ||
    t('CONVERSATION.REPLYBOX.IMAGE_DESCRIPTION_PENDING')
);

const isBlocked = computed(
  () =>
    imageModeration.value?.blocked === true &&
    imageModeration.value?.approved !== true
);
const isModerationPending = computed(
  () => imageModeration.value?.status === 'pending'
);
const isKeptBlocked = computed(
  () =>
    imageModeration.value?.review_decision === 'keep_blocked' ||
    imageModeration.value?.reviewDecision === 'keep_blocked'
);
const canBlockImage = computed(() => messageType.value === 0);
const moderationCategory = computed(() => {
  const category = imageModeration.value?.category;
  const knownCategories = [
    'pending',
    'nudity',
    'sexual',
    'violence',
    'self_harm',
    'hate',
    'illegal',
    'other_sensitive',
  ];
  // Categories are restricted to the fixed allowlist above, with an unknown fallback.
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(
    `CONVERSATION.REPLYBOX.CONTENT_SAFETY_CATEGORIES.${knownCategories.includes(category) ? category : 'unknown'}`
  );
});

watch(
  attachment,
  value => {
    imageDescription.value =
      value?.imageDescription || value?.image_description || '';
    imageModeration.value =
      value?.imageModeration || value?.image_moderation || {};
  },
  { immediate: true }
);

onMounted(() => {
  if (attachment.value?.dataUrl && !isBlocked.value) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

watch(isBlocked, blocked => {
  if (!blocked && attachment.value?.dataUrl && !isLoaded.value) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};

const announce = (message, priority = 'polite') => {
  window.dispatchEvent(
    new CustomEvent('a11y-announce', { detail: { message, priority } })
  );
};

const describeImage = async () => {
  if (isDescribing.value) return;

  try {
    isDescribing.value = true;
    announce(t('CONVERSATION.REPLYBOX.IMAGE_DESCRIPTION_LOADING'));
    const { data } = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/lumerai/tasks/describe_image`,
      { attachment_id: attachment.value.id }
    );
    imageDescription.value = data.description;
    imageModeration.value = data.moderation || {};
    if (data.cached) {
      useAlert(t('CONVERSATION.REPLYBOX.DESCRIPTION_FROM_CACHE'));
    }
    announce(
      t('CONVERSATION.REPLYBOX.IMAGE_DESCRIPTION_ANNOUNCEMENT', {
        description: data.description,
      }),
      'assertive'
    );
  } catch (error) {
    announce(
      error.response?.data?.error ||
        t('CONVERSATION.REPLYBOX.IMAGE_DESCRIPTION_ERROR'),
      'assertive'
    );
  } finally {
    isDescribing.value = false;
  }
};

const reviewImage = async decision => {
  try {
    const { data } = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/lumerai_alerts/review_attachment`,
      { attachment_id: attachment.value.id, decision }
    );
    imageModeration.value = data.moderation;
    let successMessage = t('CONVERSATION.REPLYBOX.KEEP_IMAGE_HIDDEN_SUCCESS');
    if (decision === 'approve') {
      successMessage = t('CONVERSATION.REPLYBOX.AUTHORIZE_IMAGE_SUCCESS');
    } else if (data.awaiting_admin_review) {
      successMessage = t('CONVERSATION.REPLYBOX.IMAGE_AWAITING_ADMIN');
    }
    useAlert(successMessage);
    announce(successMessage, 'assertive');
  } catch (error) {
    const message =
      error.response?.data?.error ||
      t('CONVERSATION.REPLYBOX.CONTENT_SAFETY_REVIEW_ERROR');
    useAlert(message);
    announce(message, 'assertive');
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="!isBlocked && (showGallery = true)"
  >
    <div
      v-if="isBlocked"
      class="flex max-w-md flex-col gap-3 rounded-lg border border-n-ruby-7 bg-n-ruby-2 p-4 text-n-slate-12"
      @click.stop
    >
      <div class="flex items-start gap-3">
        <Icon
          aria-hidden="true"
          icon="i-lucide-shield-alert"
          class="mt-0.5 size-5 flex-shrink-0 text-n-ruby-10"
        />
        <div>
          <p class="mb-1 font-semibold">
            {{
              isModerationPending
                ? t('CONVERSATION.REPLYBOX.CONTENT_SAFETY_PENDING')
                : t('CONVERSATION.REPLYBOX.CONTENT_SAFETY_BLOCKED')
            }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            {{
              t('CONVERSATION.REPLYBOX.CONTENT_SAFETY_CATEGORY', {
                category: moderationCategory,
                reason: imageModeration.reason,
              })
            }}
          </p>
        </div>
      </div>
      <p
        v-if="imageDescription"
        role="status"
        class="mb-0 text-sm leading-relaxed text-n-slate-11"
      >
        <strong class="text-n-slate-12">
          {{ t('CONVERSATION.REPLYBOX.SAFE_IMAGE_DESCRIPTION') }}
        </strong>
        {{ imageDescription }}
      </p>
      <Button
        v-if="!imageDescription && canDescribeImage"
        data-a11y-describe-image
        sm
        outline
        slate
        icon="i-lucide-scan-eye"
        :label="
          isModerationPending
            ? t('CONVERSATION.REPLYBOX.ANALYZE_IMAGE_NOW')
            : t('CONVERSATION.REPLYBOX.DESCRIBE_IMAGE_SAFELY')
        "
        :is-loading="isDescribing"
        :disabled="isDescribing"
        @click.stop="describeImage"
      />
      <div v-if="!isModerationPending" class="flex flex-wrap gap-2">
        <Button
          v-if="!isKeptBlocked"
          sm
          solid
          ruby
          :label="t('CONVERSATION.REPLYBOX.KEEP_IMAGE_HIDDEN')"
          @click.stop="reviewImage('keep_blocked')"
        />
        <Button
          v-if="isAdmin"
          sm
          outline
          slate
          :label="t('CONVERSATION.REPLYBOX.AUTHORIZE_IMAGE')"
          @click.stop="reviewImage('approve')"
        />
      </div>
      <p v-if="isKeptBlocked" class="mb-0 text-xs text-n-slate-11">
        {{
          isAdmin
            ? t('CONVERSATION.REPLYBOX.IMAGE_BLOCK_CONFIRMED')
            : t('CONVERSATION.REPLYBOX.IMAGE_AWAITING_ADMIN')
        }}
      </p>
      <p v-if="!isAdmin" class="mb-0 text-xs text-n-slate-11">
        {{ t('CONVERSATION.REPLYBOX.IMAGE_ADMIN_UNLOCK_ONLY') }}
      </p>
    </div>
    <div
      v-else-if="hasError"
      class="flex items-center gap-1 text-center rounded-lg"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div v-else-if="isLoaded" class="relative group rounded-lg overflow-hidden">
      <img
        class="skip-context-menu"
        :class="imageClass"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
        :alt="imageAltText"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
    <div
      v-if="isLoaded && !isBlocked && (canDescribeImage || canBlockImage)"
      class="mt-2 flex flex-col items-start gap-2"
      @click.stop
    >
      <Button
        v-if="!imageDescription"
        data-a11y-describe-image
        sm
        outline
        slate
        icon="i-lucide-scan-eye"
        :label="$t('CONVERSATION.REPLYBOX.DESCRIBE_IMAGE')"
        :is-loading="isDescribing"
        :disabled="isDescribing"
        @click.stop="describeImage"
      />
      <Button
        v-if="canBlockImage"
        sm
        outline
        ruby
        icon="i-lucide-shield-alert"
        :label="$t('CONVERSATION.REPLYBOX.BLOCK_IMAGE')"
        @click.stop="reviewImage('keep_blocked')"
      />
      <p
        v-if="imageDescription"
        role="status"
        class="mb-0 text-sm leading-relaxed text-n-slate-11"
      >
        <strong>{{
          $t('CONVERSATION.REPLYBOX.IMAGE_DESCRIPTION_LABEL')
        }}</strong>
        {{ imageDescription }}
      </p>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
